'use client';

import { type CSSProperties, useEffect } from 'react';
import { isChunkLoadError, reloadForChunkError } from '@/lib/chunkReload';
import { logger } from '@/lib/logger';

/**
 * Shared logic + presentation for the app's two error boundaries
 * (app/error.tsx and app/global-error.tsx). Each boundary supplies its own
 * outer wrapper — a centered card inside the layout vs. a full-page own-<html>
 * document — and renders <ErrorFallbackContent> for the identical inner UI.
 *
 * Dependency-free by design (React + two leaf lib modules only): global-error
 * runs when the root layout / providers may themselves have failed, so this
 * must not pull in i18n or context. Colors and font use `var(--token, fallback)`
 * so the content picks up the design system when the tokens are present
 * (error.tsx, inside the themed document) and falls back to literal values when
 * they are not (global-error.tsx, which renders its own <html>).
 */

/**
 * Logs the error (chunk or not) and, on a chunk-load failure, auto-reloads to
 * fetch the current deployment. The fallback UI always renders a manual Reload
 * button, so a suppressed auto-reload (loop guard / no storage) is never a
 * dead-end. Returns whether this was a chunk-load error.
 */
export function useChunkRecovery(error: Error & { digest?: string }): boolean {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    // error.digest is the only link from a client-visible failure back to the
    // detailed server-side stack in platform logs — never drop it silently.
    // logger.error reaches console.error even in production.
    logger.error('App error boundary tripped', {
      name: error.name,
      message: error.message,
      digest: error.digest,
      chunkError,
    });
    if (chunkError) reloadForChunkError();
  }, [chunkError, error]);

  return chunkError;
}

export function ErrorFallbackContent({
  chunkError,
  onReset,
}: {
  chunkError: boolean;
  onReset: () => void;
}) {
  return (
    <div style={styles.card}>
      <h2 style={styles.title}>
        {chunkError ? 'A new version is available' : 'Something went wrong'}
      </h2>
      {/* Action-oriented, not "reloading now": the auto-reload can be
          suppressed (private-mode storage, or after the per-episode cap on a
          broken deploy), in which case the user drives the Reload button. */}
      {chunkError && <p style={styles.body}>Reload to get the latest version.</p>}
      <div style={styles.actions}>
        {!chunkError && (
          <button type="button" style={styles.secondary} onClick={onReset}>
            Try again
          </button>
        )}
        <button
          type="button"
          style={styles.primary}
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

const FONT_STACK =
  "var(--font-sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif)";

const styles: Record<string, CSSProperties> = {
  card: { maxWidth: 420, textAlign: 'center', fontFamily: FONT_STACK },
  title: {
    margin: '0 0 8px',
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--text-primary, #1a1a1a)',
  },
  body: {
    margin: '0 0 20px',
    fontSize: 14,
    lineHeight: 1.5,
    color: 'var(--text-muted, #6b7280)',
  },
  actions: { display: 'flex', gap: 12, justifyContent: 'center' },
  primary: {
    padding: '8px 16px',
    borderRadius: 8,
    border: 'none',
    background: 'var(--accent-primary, #0F425D)',
    color: '#ffffff',
    fontSize: 14,
    cursor: 'pointer',
  },
  secondary: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid var(--border-medium, #e5e7eb)',
    background: 'transparent',
    color: 'var(--text-primary, #1a1a1a)',
    fontSize: 14,
    cursor: 'pointer',
  },
};
